import { useState, useEffect } from 'react';
import api from '../utils/api';
import API_BASE from '../utils/config';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, Upload, X, Calendar, Link, CheckCircle, Globe, Loader, Pencil } from 'lucide-react';
import { useSocket } from '../context/SocketContext';

// Türk telefon numarası: boşluk/tire/+/parantez temizle, +905XXXXXXXXX formatına getir
function normalizePhone(raw) {
  let p = raw.replace(/\D/g, ''); // tüm rakam-dışı karakterleri sil
  if (p.startsWith('90') && p.length === 12) p = p.slice(2);
  if (p.startsWith('0')  && p.length === 11) p = p.slice(1);
  return '+90' + p;
}
function validatePhone(raw) {
  if (!raw.trim()) return { ok: false, msg: 'Telefon numarası gerekli' };
  const p = normalizePhone(raw);
  if (!/^\+905\d{9}$/.test(p)) return { ok: false, msg: 'Geçerli bir telefon numarası girin (örn. 0555 555 55 55)' };
  return { ok: true, val: p };
}

const AMENITY_OPTIONS = [
  'Havuz', 'Spa', 'Ücretsiz WiFi', 'Otopark', 'Kahvaltı Dahil',
  'Restoran', 'Bar', 'Fitness Salonu', 'Evcil Hayvan Kabul',
  'Çocuk Oyun Alanı', 'Deniz Manzarası', 'Plaj Erişimi', 'Klima', 'Asansör',
];

const ROOM_FEATURES = ['Balkon', 'Deniz Manzarası', 'Jakuzi', 'Klima', 'Mini Bar', 'Kasa', 'Çalışma Masası', 'Banyolu', 'Duşlu'];

function Section({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #e2e8f0' }}>{title}</h2>
      {children}
    </div>
  );
}

export default function SettingsPage() {
  const socket = useSocket();
  const [hotel, setHotel] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('general');
  const [waQrStatus, setWaQrStatus] = useState('none');
  const [waQrImage, setWaQrImage] = useState(null);
  const [newRoom, setNewRoom] = useState({ name: '', type: '', capacity: 2, basePrice: 0, description: '', features: [] });
  const [newFaq, setNewFaq] = useState({ question: '', answer: '', category: '' });
  const [newCampaign, setNewCampaign] = useState({ title: '', description: '', discountPercent: '', validFrom: '', validUntil: '' });
  const [uploadingRoom, setUploadingRoom] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null); // { id, name, type, capacity, basePrice, description, features }
  const [importUrl, setImportUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [applyingImport, setApplyingImport] = useState(false);

  useEffect(() => {
    api.get('/hotels/me').then(h => {
      setHotel(h);
      setRooms(h.rooms || []);
      setFaqs(h.faqs || []);
      setCampaigns(h.campaigns || []);
    });

    // Google OAuth callback URL parametresini işle
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab) setActiveTab(tab);
    if (params.get('google') === 'connected') toast.success('Google Calendar başarıyla bağlandı!');
    if (tab || params.get('google')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('wa-qr', ({ qr }) => { setWaQrImage(qr); setWaQrStatus('qr'); });
    socket.on('wa-status', ({ status }) => {
      setWaQrStatus(status);
      if (status === 'connected') { setWaQrImage(null); toast.success('WhatsApp QR bağlandı!'); }
      if (status === 'disconnected') toast.error('WhatsApp bağlantısı koptu!');
    });
    return () => { socket.off('wa-qr'); socket.off('wa-status'); };
  }, [socket]);

  const connectWhatsAppQr = async () => {
    setWaQrStatus('connecting');
    try {
      await api.post('/whatsapp-qr/connect');
      const poll = setInterval(async () => {
        try {
          const data = await api.get('/whatsapp-qr/status');
          if (data.qr) { setWaQrImage(data.qr); setWaQrStatus('qr'); }
          if (data.status === 'connected') { setWaQrStatus('connected'); clearInterval(poll); }
        } catch {}
      }, 3000);
      setTimeout(() => clearInterval(poll), 120000);
    } catch { toast.error('Bağlantı başlatılamadı'); setWaQrStatus('none'); }
  };

  const disconnectWhatsAppQr = async () => {
    try {
      await api.post('/whatsapp-qr/disconnect');
      setWaQrStatus('none');
      setWaQrImage(null);
      toast.success('WhatsApp QR bağlantısı kesildi');
    } catch { toast.error('Bağlantı kesilemedi'); }
  };

  const saveHotelSettings = async () => {
    try {
      let payload = { ...hotel };
      // notificationPhone varsa normalize et ve doğrula
      if (payload.notificationPhone && payload.notificationPhone.trim()) {
        const result = validatePhone(payload.notificationPhone);
        if (!result.ok) { toast.error('Bildirim Numarası: ' + result.msg); return; }
        payload.notificationPhone = result.val;
      } else {
        payload.notificationPhone = payload.notificationPhone?.trim() || null;
      }
      await api.put('/hotels/me', payload);
      setHotel(h => ({ ...h, notificationPhone: payload.notificationPhone }));
      toast.success('Ayarlar kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
  };

  const saveAmenities = async () => {
    try {
      const amenities = (() => { try { return JSON.parse(hotel.amenities || '[]'); } catch { return []; } })();
      await api.put('/hotels/me/amenities', { amenities });
      toast.success('İmkanlar kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
  };

  const toggleAmenity = (a) => {
    const current = (() => { try { return JSON.parse(hotel.amenities || '[]'); } catch { return []; } })();
    const updated = current.includes(a) ? current.filter(x => x !== a) : [...current, a];
    setHotel({ ...hotel, amenities: JSON.stringify(updated) });
  };

  const addRoom = async () => {
    if (!newRoom.name || !newRoom.basePrice) return toast.error('Oda adı ve fiyat gerekli');
    try {
      const room = await api.post('/hotels/rooms', { ...newRoom });
      setRooms([...rooms, room]);
      setNewRoom({ name: '', type: '', capacity: 2, basePrice: 0, description: '', features: [] });
      toast.success('Oda eklendi');
    } catch { toast.error('Eklenemedi'); }
  };

  const deleteRoom = async (id) => {
    try {
      await api.delete(`/hotels/rooms/${id}`);
      setRooms(rooms.filter(r => r.id !== id));
      toast.success('Oda silindi');
    } catch { toast.error('Silinemedi'); }
  };

  const saveRoomEdit = async () => {
    if (!editingRoom) return;
    try {
      await api.put(`/hotels/rooms/${editingRoom.id}`, {
        name: editingRoom.name,
        type: editingRoom.type,
        capacity: editingRoom.capacity,
        basePrice: editingRoom.basePrice,
        description: editingRoom.description,
        features: JSON.stringify(editingRoom.features || []),
      });
      setRooms(rooms.map(r => r.id === editingRoom.id ? { ...r, ...editingRoom, features: JSON.stringify(editingRoom.features || []) } : r));
      setEditingRoom(null);
      toast.success('Oda güncellendi');
    } catch { toast.error('Güncellenemedi'); }
  };

  const addFaq = async () => {
    if (!newFaq.question || !newFaq.answer) return toast.error('Soru ve cevap gerekli');
    try {
      const faq = await api.post('/hotels/faqs', newFaq);
      setFaqs([...faqs, faq]);
      setNewFaq({ question: '', answer: '', category: '' });
      toast.success('SSS eklendi');
    } catch { toast.error('Eklenemedi'); }
  };

  const deleteFaq = async (id) => {
    try {
      await api.delete(`/hotels/faqs/${id}`);
      setFaqs(faqs.filter(f => f.id !== id));
      toast.success('Silindi');
    } catch { toast.error('Silinemedi'); }
  };

  const addCampaign = async () => {
    if (!newCampaign.title || !newCampaign.validFrom || !newCampaign.validUntil) return toast.error('Başlık ve tarihler gerekli');
    try {
      const campaign = await api.post('/hotels/campaigns', newCampaign);
      setCampaigns([...campaigns, campaign]);
      setNewCampaign({ title: '', description: '', discountPercent: '', validFrom: '', validUntil: '' });
      toast.success('Kampanya eklendi');
    } catch { toast.error('Eklenemedi'); }
  };

  const deleteCampaign = async (id) => {
    try {
      await api.delete(`/hotels/campaigns/${id}`);
      setCampaigns(campaigns.filter(c => c.id !== id));
      toast.success('Kampanya silindi');
    } catch { toast.error('Silinemedi'); }
  };

  const uploadImages = async (roomId, files) => {
    setUploadingRoom(roomId);
    const formData = new FormData();
    Array.from(files).forEach(f => formData.append('images', f));
    try {
      const updated = await api.post(`/hotels/rooms/${roomId}/images`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setRooms(rooms.map(r => r.id === roomId ? { ...r, images: updated.images } : r));
      toast.success('Görseller yüklendi');
    } catch { toast.error('Yükleme başarısız'); }
    finally { setUploadingRoom(null); }
  };

  const deleteImage = async (roomId, idx) => {
    try {
      const updated = await api.delete(`/hotels/rooms/${roomId}/images/${idx}`);
      setRooms(rooms.map(r => r.id === roomId ? { ...r, images: updated.images } : r));
      toast.success('Görsel silindi');
    } catch { toast.error('Silinemedi'); }
  };

  const connectGoogle = () => {
    window.location.href = `${API_BASE}/api/auth/google`;
  };

  const openMetaPopup = (url, channel) => {
    const w = 600, h = 700;
    const left = Math.round(window.screenX + (window.outerWidth - w) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - h) / 2);
    const popup = window.open(url, `meta-${channel}`, `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`);

    const onMessage = (e) => {
      if (e.data?.type === 'meta-oauth-success' && e.data.channel === channel) {
        window.removeEventListener('message', onMessage);
        api.get('/hotels/me').then(h => {
          setHotel(h);
          toast.success(channel === 'instagram' ? 'Instagram başarıyla bağlandı!' : 'WhatsApp başarıyla bağlandı!');
        });
      }
      if (e.data?.type === 'meta-oauth-error' && e.data.channel === channel) {
        window.removeEventListener('message', onMessage);
        toast.error(e.data.message || 'Bağlantı başarısız');
      }
    };
    window.addEventListener('message', onMessage);

    // Popup kapanırsa listener temizle
    const timer = setInterval(() => {
      if (popup?.closed) { clearInterval(timer); window.removeEventListener('message', onMessage); }
    }, 1000);
  };

  const connectInstagram = async () => {
    try {
      const data = await api.get('/auth/meta/instagram');
      openMetaPopup(data.url, 'instagram');
    } catch { toast.error('Instagram bağlantısı başlatılamadı'); }
  };

  const disconnectInstagram = async () => {
    try {
      await api.delete('/auth/meta/instagram');
      setHotel({ ...hotel, instagramPageId: null, instagramUsername: null });
      toast.success('Instagram bağlantısı kesildi');
    } catch { toast.error('Bağlantı kesilemedi'); }
  };

  const connectWhatsApp = async () => {
    try {
      const data = await api.get('/auth/meta/whatsapp');
      openMetaPopup(data.url, 'whatsapp');
    } catch { toast.error('WhatsApp bağlantısı başlatılamadı'); }
  };

  const disconnectWhatsApp = async () => {
    try {
      await api.delete('/auth/meta/whatsapp');
      setHotel({ ...hotel, whatsappPhoneId: null, whatsappPhoneNumber: null });
      toast.success('WhatsApp bağlantısı kesildi');
    } catch { toast.error('Bağlantı kesilemedi'); }
  };

  const disconnectGoogle = async () => {
    try {
      await api.delete('/auth/google');
      setHotel({ ...hotel, googleCalendarId: null, googleAccessToken: null });
      toast.success('Google bağlantısı kesildi');
    } catch { toast.error('Bağlantı kesilemedi'); }
  };

  const handleImport = async () => {
    if (!importUrl.trim()) return toast.error('URL girin');
    setImporting(true);
    setImportPreview(null);
    try {
      const data = await api.post('/hotels/import-from-url', { url: importUrl });
      setImportPreview(data.data);
      toast.success('Veri çekildi! İnceleyin ve onaylayın.');
    } catch (e) {
      toast.error(e?.error || 'İçe aktarma başarısız');
    }
    setImporting(false);
  };

  const applyImport = async () => {
    if (!importPreview) return;
    setApplyingImport(true);
    try {
      const updates = {};
      if (importPreview.name) updates.name = importPreview.name;
      if (importPreview.description) updates.description = importPreview.description;
      if (importPreview.phone) updates.phone = importPreview.phone;
      if (importPreview.email) updates.email = importPreview.email;
      if (importPreview.address) updates.address = importPreview.address;
      if (importPreview.city) updates.city = importPreview.city;
      if (Object.keys(updates).length) {
        const updated = { ...hotel, ...updates };
        await api.put('/hotels/me', updated);
        setHotel(updated);
      }
      // Odaları ekle
      const importRooms = importPreview.rooms || [];
      for (const room of importRooms) {
        try {
          const r = await api.post('/hotels/rooms', { ...room, features: room.features || [] });
          setRooms(prev => [...prev, r]);
          
          // Odaya ait görseller indirildiyse hemen kaydet
          if (room.downloadedImages && room.downloadedImages.length > 0) {
            try {
              const imgResp = await api.put(`/hotels/rooms/${r.id}/images-bulk`, { images: room.downloadedImages });
              setRooms(prev => prev.map(cr => cr.id === r.id ? { ...cr, images: JSON.stringify(imgResp.images) } : cr));
            } catch (err) {
              console.error('Oda görselleri eklenemedi:', err);
            }
          }
        } catch (err) {
          console.error('Oda oluşturulamadı:', err);
          toast.error(`${room.name || 'Oda'} oluşturulamadı.`);
        }
      }
      // SSS ekle
      for (const faq of (importPreview.faqs || [])) {
        try {
          const f = await api.post('/hotels/faqs', faq);
          setFaqs(prev => [...prev, f]);
        } catch {}
      }
      setImportPreview(null);
      setImportUrl('');
      toast.success('Veriler başarıyla aktarıldı!');
    } catch { toast.error('Aktarma başarısız'); }
    setApplyingImport(false);
  };

  if (!hotel) return <div style={{ padding: 28, color: '#94a3b8' }}>Yükleniyor...</div>;

  const amenities = (() => { try { return JSON.parse(hotel.amenities || '[]'); } catch { return []; } })();

  const tabs = [
    { id: 'general', label: 'Genel Ayarlar' },
    { id: 'rooms', label: 'Odalar' },
    { id: 'faqs', label: 'SSS' },
    { id: 'campaigns', label: 'Kampanyalar' },
    { id: 'deposit', label: '💳 Ödeme' },
    { id: 'import', label: 'Web\'den İçe Aktar' },
    { id: 'channels', label: 'Kanal Bağlantıları' },
  ];

  return (
    <div style={{ padding: 28 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 20 }}>Ayarlar</h1>

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #e2e8f0', overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500,
            color: activeTab === t.id ? '#3b82f6' : '#64748b', whiteSpace: 'nowrap',
            borderBottom: activeTab === t.id ? '2px solid #3b82f6' : '2px solid transparent',
            marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'general' && (
        <>
        <Section title="Otel Bilgileri">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div><label>Otel Adı</label><input value={hotel.name || ''} onChange={e => setHotel({...hotel, name: e.target.value})} /></div>
            <div><label>Telefon</label><input value={hotel.phone || ''} onChange={e => setHotel({...hotel, phone: e.target.value})} /></div>
            <div><label>E-posta</label><input value={hotel.email || ''} onChange={e => setHotel({...hotel, email: e.target.value})} /></div>
            <div>
              <label>📲 Bildirim Numarası</label>
              <input
                placeholder="+905xxxxxxxxx (rezervasyon gelince bildirim alacak numara)"
                value={hotel.notificationPhone || ''}
                onChange={e => setHotel({...hotel, notificationPhone: e.target.value})}
              />
              <span style={{ fontSize: 12, color: '#64748b' }}>WhatsApp bot üzerinden otelci bu numaraya anlık bildirim alır.</span>
            </div>
            <div><label>Şehir</label><input value={hotel.city || ''} onChange={e => setHotel({...hotel, city: e.target.value})} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label>Adres</label><input value={hotel.address || ''} onChange={e => setHotel({...hotel, address: e.target.value})} /></div>
            <div style={{ gridColumn: '1 / -1' }}><label>Otel Hakkında</label><textarea rows={3} value={hotel.description || ''} onChange={e => setHotel({...hotel, description: e.target.value})} /></div>
          </div>
          <button className="btn btn-primary" onClick={saveHotelSettings} style={{ marginTop: 16 }}><Save size={15} /> Kaydet</button>
        </Section>

        <Section title="Otel İmkanları">
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>Seçilen imkanlar bot tarafından müşterilere otomatik olarak aktarılır.</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            {AMENITY_OPTIONS.map(a => (
              <button key={a} onClick={() => toggleAmenity(a)} style={{
                padding: '6px 14px', border: `1.5px solid ${amenities.includes(a) ? '#3b82f6' : '#e2e8f0'}`,
                borderRadius: 20, background: amenities.includes(a) ? '#eff6ff' : 'white',
                color: amenities.includes(a) ? '#1d4ed8' : '#64748b',
                fontSize: 13, cursor: 'pointer', fontWeight: amenities.includes(a) ? 600 : 400,
                transition: 'all 0.15s',
              }}>{a}</button>
            ))}
          </div>
          {amenities.length > 0 && (
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
              Seçili: <strong>{amenities.join(', ')}</strong>
            </div>
          )}
          <button className="btn btn-primary" onClick={saveAmenities}><Save size={15} /> İmkanları Kaydet</button>
        </Section>
        </>
      )}

      {activeTab === 'rooms' && (
        <Section title="Oda Yönetimi">
          {rooms.map(room => {
            const images = (() => { try { return JSON.parse(room.images || '[]'); } catch { return []; } })();
            const features = (() => { try { const p = JSON.parse(room.features || '[]'); return Array.isArray(p) ? p : JSON.parse(p); } catch { return []; } })();
            return (
              <div key={room.id} style={{ padding: '16px 0', borderBottom: '1px solid #e2e8f0' }}>
                {editingRoom?.id === room.id ? (
                  <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 110px', gap: 8, marginBottom: 8 }}>
                      <div><label style={{ fontSize: 12 }}>Oda Adı</label><input value={editingRoom.name} onChange={e => setEditingRoom({ ...editingRoom, name: e.target.value })} /></div>
                      <div><label style={{ fontSize: 12 }}>Tip</label><input value={editingRoom.type} onChange={e => setEditingRoom({ ...editingRoom, type: e.target.value })} /></div>
                      <div><label style={{ fontSize: 12 }}>Kapasite</label><input type="number" value={editingRoom.capacity} onChange={e => setEditingRoom({ ...editingRoom, capacity: parseInt(e.target.value) })} /></div>
                      <div><label style={{ fontSize: 12 }}>Fiyat (₺)</label><input type="number" value={editingRoom.basePrice} onChange={e => setEditingRoom({ ...editingRoom, basePrice: parseFloat(e.target.value) })} /></div>
                    </div>
                    <textarea placeholder="Açıklama..." rows={2} value={editingRoom.description || ''} onChange={e => setEditingRoom({ ...editingRoom, description: e.target.value })} style={{ marginBottom: 8 }} />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                      {ROOM_FEATURES.map(f => (
                        <button key={f} type="button" onClick={() => {
                          const updated = (editingRoom.features || []).includes(f) ? editingRoom.features.filter(x => x !== f) : [...(editingRoom.features || []), f];
                          setEditingRoom({ ...editingRoom, features: updated });
                        }} style={{ padding: '3px 10px', border: `1.5px solid ${(editingRoom.features || []).includes(f) ? '#3b82f6' : '#e2e8f0'}`, borderRadius: 20, background: (editingRoom.features || []).includes(f) ? '#eff6ff' : 'white', color: (editingRoom.features || []).includes(f) ? '#1d4ed8' : '#64748b', fontSize: 12, cursor: 'pointer' }}>{f}</button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={saveRoomEdit}><Save size={13} /> Kaydet</button>
                      <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={() => setEditingRoom(null)}>İptal</button>
                    </div>
                  </div>
                ) : (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 500 }}>{room.name} <span style={{ fontSize: 12, color: '#64748b' }}>({room.type})</span></div>
                    <div style={{ fontSize: 13, color: '#64748b' }}>{room.capacity} kişi · ₺{room.basePrice}/gece</div>
                    {features.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                        {features.map(f => (
                          <span key={f} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#f1f5f9', color: '#475569' }}>{f}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" style={{ padding: '6px 10px' }} onClick={() => setEditingRoom({ id: room.id, name: room.name, type: room.type, capacity: room.capacity, basePrice: room.basePrice, description: room.description || '', features })}><Pencil size={14} /></button>
                    <label style={{ cursor: 'pointer' }}>
                      <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => uploadImages(room.id, e.target.files)} />
                      <span className="btn btn-secondary" style={{ padding: '6px 10px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                        {uploadingRoom === room.id ? 'Yükleniyor...' : <><Upload size={13} /> Görsel</>}
                      </span>
                    </label>
                    <button className="btn btn-danger" style={{ padding: '6px 10px' }} onClick={() => deleteRoom(room.id)}><Trash2 size={14} /></button>
                  </div>
                </div>
                )}
                {images.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                    {images.map((src, idx) => (
                      <div key={idx} style={{ position: 'relative', width: 90, height: 70 }}>
                        <img src={`${API_BASE}${src}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} />
                        <button onClick={() => deleteImage(room.id, idx)} style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                          <X size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div style={{ marginTop: 20, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Yeni Oda Ekle</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px 100px', gap: 10 }}>
              <div><label>Oda Adı</label><input value={newRoom.name} onChange={e => setNewRoom({...newRoom, name: e.target.value})} placeholder="Standart Oda" /></div>
              <div><label>Tip</label><input value={newRoom.type} onChange={e => setNewRoom({...newRoom, type: e.target.value})} placeholder="Deniz Manzaralı" /></div>
              <div><label>Kapasite</label><input type="number" value={newRoom.capacity} onChange={e => setNewRoom({...newRoom, capacity: parseInt(e.target.value)})} /></div>
              <div><label>Fiyat (₺)</label><input type="number" value={newRoom.basePrice} onChange={e => setNewRoom({...newRoom, basePrice: parseFloat(e.target.value)})} /></div>
            </div>
            <textarea placeholder="Oda açıklaması..." rows={2} value={newRoom.description} onChange={e => setNewRoom({...newRoom, description: e.target.value})} style={{ marginTop: 10 }} />
            <div style={{ marginTop: 10 }}>
              <label style={{ fontSize: 13, color: '#64748b', marginBottom: 6, display: 'block' }}>Oda Özellikleri</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {ROOM_FEATURES.map(f => (
                  <button key={f} type="button" onClick={() => {
                    const updated = newRoom.features.includes(f) ? newRoom.features.filter(x => x !== f) : [...newRoom.features, f];
                    setNewRoom({...newRoom, features: updated});
                  }} style={{
                    padding: '4px 10px', border: `1.5px solid ${newRoom.features.includes(f) ? '#3b82f6' : '#e2e8f0'}`,
                    borderRadius: 20, background: newRoom.features.includes(f) ? '#eff6ff' : 'white',
                    color: newRoom.features.includes(f) ? '#1d4ed8' : '#64748b', fontSize: 12, cursor: 'pointer',
                  }}>{f}</button>
                ))}
              </div>
            </div>
            <button className="btn btn-primary" onClick={addRoom} style={{ marginTop: 12 }}><Plus size={15} /> Oda Ekle</button>
          </div>
        </Section>
      )}

      {activeTab === 'faqs' && (
        <Section title="Sıkça Sorulan Sorular">
          {faqs.map(faq => (
            <div key={faq.id} style={{ padding: '12px 0', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{faq.question}</div>
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{faq.answer}</div>
              </div>
              <button className="btn btn-danger" style={{ padding: '6px 10px', flexShrink: 0 }} onClick={() => deleteFaq(faq.id)}><Trash2 size={14} /></button>
            </div>
          ))}
          <div style={{ marginTop: 20, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Yeni Soru Ekle</h3>
            <div style={{ marginBottom: 10 }}><label>Soru</label><input value={newFaq.question} onChange={e => setNewFaq({...newFaq, question: e.target.value})} placeholder="Check-in saati nedir?" /></div>
            <div style={{ marginBottom: 10 }}><label>Cevap</label><textarea rows={2} value={newFaq.answer} onChange={e => setNewFaq({...newFaq, answer: e.target.value})} placeholder="Check-in saatimiz 14:00..." /></div>
            <button className="btn btn-primary" onClick={addFaq}><Plus size={15} /> Ekle</button>
          </div>
        </Section>
      )}

      {activeTab === 'campaigns' && (
        <Section title="Kampanya Yönetimi">
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>Aktif kampanyalar bot tarafından müşterilere otomatik olarak iletilir.</p>
          {campaigns.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>Henüz kampanya yok</div>}
          {campaigns.map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid #e2e8f0' }}>
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{c.title}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                  {c.discountPercent ? `%${c.discountPercent} indirim` : ''} ·{' '}
                  {new Date(c.validFrom).toLocaleDateString('tr-TR')} — {new Date(c.validUntil).toLocaleDateString('tr-TR')}
                </div>
                {c.description && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{c.description}</div>}
              </div>
              <button className="btn btn-danger" style={{ padding: '6px 10px' }} onClick={() => deleteCampaign(c.id)}><Trash2 size={14} /></button>
            </div>
          ))}
          <div style={{ marginTop: 20, padding: 16, background: '#f8fafc', borderRadius: 8 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Yeni Kampanya</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={{ gridColumn: '1 / -1' }}><label>Kampanya Başlığı</label><input value={newCampaign.title} onChange={e => setNewCampaign({...newCampaign, title: e.target.value})} placeholder="Erken rezervasyon indirimi" /></div>
              <div><label>İndirim %</label><input type="number" value={newCampaign.discountPercent} onChange={e => setNewCampaign({...newCampaign, discountPercent: e.target.value})} placeholder="20" /></div>
              <div style={{ gridColumn: '1 / -1' }}><label>Açıklama</label><input value={newCampaign.description} onChange={e => setNewCampaign({...newCampaign, description: e.target.value})} placeholder="30 gün önceden rezervasyon yapanlara özel" /></div>
              <div><label>Başlangıç</label><input type="date" value={newCampaign.validFrom} onChange={e => setNewCampaign({...newCampaign, validFrom: e.target.value})} /></div>
              <div><label>Bitiş</label><input type="date" value={newCampaign.validUntil} onChange={e => setNewCampaign({...newCampaign, validUntil: e.target.value})} /></div>
            </div>
            <button className="btn btn-primary" onClick={addCampaign} style={{ marginTop: 12 }}><Plus size={15} /> Kampanya Ekle</button>
          </div>
        </Section>
      )}

      {activeTab === 'import' && (
        <Section title="Web Sitesinden Otomatik İçe Aktar">
          <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16, lineHeight: 1.6 }}>
            Otel web sitenizin, Booking.com veya Tripadvisor sayfanızın URL'sini girin. Sistem sayfayı okuyup otel adı, açıklama, odalar ve SSS bilgilerini otomatik olarak dolduracak.
          </p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            <input value={importUrl} onChange={e => setImportUrl(e.target.value)} placeholder="https://www.oteliniz.com" style={{ flex: 1 }} disabled={importing} />
            <button className="btn btn-primary" onClick={handleImport} disabled={importing} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {importing ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Okunuyor...</> : <><Globe size={15} /> Çek</>}
            </button>
          </div>

          {importPreview && (
            <div style={{ border: '2px solid #3b82f6', borderRadius: 10, padding: 20, background: '#eff6ff' }}>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 14, color: '#1d4ed8' }}>Önizleme — Verileri İnceleyin</div>
              {importPreview.name && <div style={{ marginBottom: 8 }}><strong>Otel Adı:</strong> {importPreview.name}</div>}
              {importPreview.city && <div style={{ marginBottom: 8 }}><strong>Şehir:</strong> {importPreview.city}</div>}
              {importPreview.phone && <div style={{ marginBottom: 8 }}><strong>Telefon:</strong> {importPreview.phone}</div>}
              {importPreview.description && (
                <div style={{ marginBottom: 8 }}>
                  <strong>Açıklama:</strong>
                  <div style={{ fontSize: 13, color: '#475569', marginTop: 4, lineHeight: 1.6 }}>{importPreview.description}</div>
                </div>
              )}
              {importPreview.rooms?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <strong>Odalar ({importPreview.rooms.length} adet):</strong>
                  <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {importPreview.rooms.map((r, i) => (
                      <div key={i} style={{ fontSize: 13, color: '#475569', padding: '10px', background: 'white', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                        <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>
                          {r.name} <span style={{ fontWeight: 400, color: '#64748b' }}>— {r.capacity} kişi, ₺{r.basePrice}/gece</span>
                        </div>
                        {r.downloadedImages?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {r.downloadedImages.slice(0, 5).map((src, idx) => (
                              <img key={idx} src={`${API_BASE}${src}`} alt="" style={{ width: 80, height: 55, objectFit: 'cover', borderRadius: 6, border: '1px solid #dbeafe' }} />
                            ))}
                            {r.downloadedImages.length > 5 && (
                              <div style={{ width: 80, height: 55, borderRadius: 6, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#64748b', fontWeight: 500 }}>
                                +{r.downloadedImages.length - 5}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {importPreview.faqs?.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <strong>SSS ({importPreview.faqs.length} soru):</strong>
                  <div style={{ marginTop: 6, fontSize: 13, color: '#475569' }}>
                    {importPreview.faqs.map((f, i) => <div key={i} style={{ padding: '3px 0' }}>• {f.question}</div>)}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn btn-primary" onClick={applyImport} disabled={applyingImport}>
                  {applyingImport ? 'Aktarılıyor...' : 'Onayla ve Kaydet'}
                </button>
                <button className="btn btn-secondary" onClick={() => setImportPreview(null)}>İptal</button>
              </div>
            </div>
          )}
        </Section>
      )}

      {activeTab === 'channels' && (
        <>
        <Section title="Instagram">
          {hotel.instagramPageId ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1px solid #86efac', borderRadius: 10, marginBottom: 16 }}>
                <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(135deg,#f43f5e,#a855f7,#f97316)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 20 }}>📷</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#15803d' }}>Bağlı</span>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 15, color: '#1e293b' }}>
                    {hotel.instagramUsername ? `@${hotel.instagramUsername}` : 'Instagram Business'}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>DM'ler yapay zeka ile otomatik yanıtlanıyor</div>
                </div>
              </div>
              <button className="btn btn-danger" style={{ fontSize: 13 }} onClick={disconnectInstagram}>Bağlantıyı Kes</button>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 18px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, marginBottom: 18 }}>
                <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 20 }}>📷</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b', marginBottom: 4 }}>Instagram Business Hesabı</div>
                  <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                    Facebook hesabınızla giriş yapın, Instagram Business hesabınızı seçin ve izin verin — bot otomatik devreye girer.
                  </div>
                </div>
              </div>
              <button className="btn btn-primary" onClick={connectInstagram} style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                <Globe size={15} /> Instagram'ı Bağla
              </button>
            </div>
          )}
        </Section>

        <Section title="WhatsApp Business">
          {hotel.whatsappPhoneId ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1px solid #86efac', borderRadius: 10, marginBottom: 16 }}>
                <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#25d366', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: 22 }}>💬</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#15803d' }}>Bağlı</span>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 15, color: '#1e293b' }}>
                    {hotel.whatsappPhoneNumber || hotel.whatsappPhoneId}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Mesajlar yapay zeka ile otomatik yanıtlanıyor</div>
                </div>
              </div>
              <button className="btn btn-danger" style={{ fontSize: 13 }} onClick={disconnectWhatsApp}>Bağlantıyı Kes</button>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 18px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10, marginBottom: 18 }}>
                <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 22 }}>💬</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b', marginBottom: 4 }}>WhatsApp Business Hesabı</div>
                  <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
                    WhatsApp Business hesabınızı seçin ve izin verin — müşteri mesajları anında yapay zekaya yönlendirilir.
                  </div>
                </div>
              </div>
              <button className="btn btn-primary" onClick={connectWhatsApp} style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                <Globe size={15} /> WhatsApp'ı Bağla
              </button>
            </div>
          )}
        </Section>

        <Section title="Google Calendar">
          {hotel.googleCalendarId ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, marginBottom: 16 }}>
                <CheckCircle size={18} color="#16a34a" />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#15803d' }}>Google Calendar Bağlı</div>
                  <div style={{ fontSize: 12, color: '#16a34a' }}>Rezervasyon onaylandığında otomatik etkinlik oluşturulur</div>
                </div>
              </div>
              <button className="btn btn-danger" style={{ fontSize: 13 }} onClick={disconnectGoogle}>Bağlantıyı Kes</button>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: 14, color: '#64748b', marginBottom: 16, lineHeight: 1.6 }}>
                Google hesabınızı bağlayarak rezervasyon onaylandığında takvime otomatik etkinlik ekleyin.
              </p>
              <button className="btn btn-primary" onClick={connectGoogle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Calendar size={15} /> Google ile Bağlan
              </button>
            </div>
          )}
        </Section>

        <Section title="WhatsApp (Hızlı Bağlantı — QR)">
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.6 }}>
            Resmi Meta doğrulaması tamamlanana kadar mevcut WhatsApp numaranızı QR ile bağlayıp botu çalıştırabilirsiniz.
            Doğrulama tamamlandığında resmi API'ye ücretsiz geçiş yapılır.
          </p>
          {waQrStatus === 'connected' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1px solid #86efac', borderRadius: 10, marginBottom: 16 }}>
              <div style={{ width: 42, height: 42, borderRadius: '50%', background: '#25d366', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: 22 }}>💬</span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
                  <span style={{ fontWeight: 700, fontSize: 14, color: '#15803d' }}>Bağlı (QR)</span>
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Mesajlar yapay zeka ile otomatik yanıtlanıyor</div>
              </div>
            </div>
          )}
          {waQrStatus === 'qr' && waQrImage && (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b', marginBottom: 12 }}>QR kodu telefonda okutun</div>
              <img src={waQrImage} alt="WhatsApp QR" style={{ width: 220, height: 220, borderRadius: 12, border: '2px solid #e2e8f0' }} />
              <p style={{ fontSize: 13, color: '#64748b', marginTop: 12 }}>Telefonda <strong>WhatsApp → Bağlı Cihazlar → Cihaz Bağla</strong> ile okutun</p>
            </div>
          )}
          {waQrStatus === 'connecting' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', fontSize: 13, padding: '12px 0' }}>
              <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> QR hazırlanıyor...
            </div>
          )}
          <div style={{ display: 'flex', gap: 10 }}>
            {(waQrStatus === 'none' || waQrStatus === 'disconnected') && (
              <button className="btn btn-primary" onClick={connectWhatsAppQr} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Globe size={15} /> QR ile Bağlan
              </button>
            )}
            {waQrStatus === 'connected' && (
              <button className="btn btn-danger" style={{ fontSize: 13 }} onClick={disconnectWhatsAppQr}>Bağlantıyı Kes</button>
            )}
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 12, lineHeight: 1.6 }}>
            ⚠️ Bu yöntem resmi olmayan bir köprüdür. Hız limiti ve insansı gecikmeler uygulanır, ancak düşük olasılıkla numara kısıtlanabilir.
          </p>
        </Section>

        <Section title="iCal Senkronizasyon URL'si">
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12, lineHeight: 1.6 }}>
            Bu URL'yi Booking.com, Airbnb veya herhangi bir takvim uygulamasına ekleyerek rezervasyonlarınızı otomatik senkronize edin.
          </p>
          {hotel.slug && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{ flex: 1, background: '#f1f5f9', padding: '9px 12px', borderRadius: 6, fontSize: 12, color: '#334155', wordBreak: 'break-all' }}>
                {`${API_BASE}/api/calendar/export/${hotel.slug}.ics`}
              </code>
              <button className="btn btn-secondary" style={{ fontSize: 12, flexShrink: 0 }}
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/calendar/export/${hotel.slug}.ics`); toast.success('Kopyalandı!'); }}>
                <Link size={13} /> Kopyala
              </button>
            </div>
          )}
        </Section>
        </>
      )}

      {activeTab === 'deposit' && (
        <>
        <Section title="Ödeme / Ön Ödeme Ayarları">
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 20, lineHeight: 1.6 }}>
            Etkinleştirildiğinde, bot rezervasyon talebini aldıktan sonra misafire otomatik ödeme mesajı gönderir.
            Misafir ödeme yaptıktan sonra siz dekontu onaylarsınız, rezervasyon kesinleşir.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, padding: '14px 18px', background: hotel.depositEnabled ? '#f0fdf4' : '#f8fafc', border: `1px solid ${hotel.depositEnabled ? '#bbf7d0' : '#e2e8f0'}`, borderRadius: 10 }}>
            <input
              type="checkbox"
              id="depositEnabled"
              checked={!!hotel.depositEnabled}
              onChange={e => setHotel({ ...hotel, depositEnabled: e.target.checked })}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
            <label htmlFor="depositEnabled" style={{ fontWeight: 600, fontSize: 14, cursor: 'pointer', color: hotel.depositEnabled ? '#15803d' : '#1e293b' }}>
              {hotel.depositEnabled ? '✅ Ödeme aktif — bot rezervasyon sonrası ödeme mesajı gönderiyor' : 'Ön ödeme sistemini etkinleştir'}
            </label>
          </div>

          {hotel.depositEnabled && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <label>Ödeme Tutarı (₺)</label>
                <input
                  type="number"
                  min="0"
                  value={hotel.depositAmount || ''}
                  onChange={e => setHotel({ ...hotel, depositAmount: parseFloat(e.target.value) || null })}
                  placeholder="Örn: 500"
                />
                <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Bot bu tutarı misafire bildirir</p>
              </div>
              <div>
                <label>Hesap Sahibi Adı</label>
                <input
                  value={hotel.depositAccountName || ''}
                  onChange={e => setHotel({ ...hotel, depositAccountName: e.target.value })}
                  placeholder="Ad Soyad / Şirket Adı"
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label>IBAN</label>
                <input
                  value={hotel.depositIban || ''}
                  onChange={e => setHotel({ ...hotel, depositIban: e.target.value })}
                  placeholder="TR00 0000 0000 0000 0000 0000 00"
                  style={{ fontFamily: 'monospace', letterSpacing: 1 }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label>Ödeme Linki (opsiyonel)</label>
                <input
                  value={hotel.depositPaymentLink || ''}
                  onChange={e => setHotel({ ...hotel, depositPaymentLink: e.target.value })}
                  placeholder="https://ödeme.linki (iyzico, PayTR vs.)"
                />
                <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>Kredi kartı ile ödeme seçeneği sunmak istiyorsanız doldurun</p>
              </div>
            </div>
          )}

          <button className="btn btn-primary" onClick={saveHotelSettings} style={{ marginTop: 20 }}><Save size={15} /> Kaydet</button>
        </Section>

        {hotel.depositEnabled && (
          <Section title="Önizleme — Bot Mesajı">
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 12 }}>Bot misafire rezervasyon bilgilerini topladıktan sonra şunu gönderecek:</p>
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, fontSize: 13, color: '#1e293b', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
{`Rezervasyon talebinizi aldık! 🎉 Yerinizi kesinleştirmek için ${hotel.depositAmount || '___'} TL ödeme gerekmektedir.

Ödeme seçenekleri:
${hotel.depositIban ? `🏦 Havale/EFT: ${hotel.depositIban}\nHesap Sahibi: ${hotel.depositAccountName || '___'}` : ''}${hotel.depositPaymentLink ? `\n💳 Kredi Kartı: ${hotel.depositPaymentLink}` : ''}

Ödeme sonrasında dekontu buraya iletirseniz rezervasyonunuz onaylanacaktır. ✅`}
            </div>
          </Section>
        )}
        </>
      )}
    </div>
  );
}
